import { ApiError } from "@/lib/api";

/**
 * Offline write queue. While `navigator.onLine` is false every write waits here instead of failing;
 * when the browser reports the network back, the waiters are released in the order they arrived.
 * Writes to one item are already serialized by `serial()` in mutations.ts, so per-item order holds
 * across the pause; writes to different items are independent.
 */

export const isOnline = () => typeof navigator === "undefined" || navigator.onLine !== false;

let waiters: (() => void)[] = [];

export function untilOnline(): Promise<void> {
  if (isOnline()) return Promise.resolve();
  return new Promise((resolve) => waiters.push(resolve));
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    const ready = waiters;
    waiters = [];
    for (const go of ready) go();
  });
}

/** Number of writes currently paused (for tests and the banner). */
export const queuedWriteCount = () => waiters.length;

/**
 * Runs a write once the browser is online. If the connection drops while the request is in flight
 * (a network error and the browser now says offline) the request is sent again after reconnecting;
 * callers that create rows pass an Idempotency-Key so that cannot duplicate them.
 */
export async function whenOnline<T>(send: () => Promise<T>): Promise<T> {
  for (;;) {
    await untilOnline();
    try {
      return await send();
    } catch (e) {
      if (e instanceof ApiError && e.isNetwork && !isOnline()) continue;
      throw e;
    }
  }
}
